import AddIcon from "@mui/icons-material/Add";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import FilterListIcon from "@mui/icons-material/FilterList";
import PanToolIcon from "@mui/icons-material/PanTool";
import GridOnIcon from "@mui/icons-material/GridOn";
import RemoveIcon from "@mui/icons-material/Remove";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import { Button, Link } from "@dmca/frontend/components/button";
import { Input, SearchBar, Select } from "@dmca/frontend/components/input";
import { ConfirmationModal } from "@dmca/frontend/components/modal";
import {
  GlobalNavbarCenter,
  GlobalNavbarRight,
  PageHeader,
} from "@dmca/frontend/components/nav";
import {
  checkPermissionsInLoader,
  useAppPermissions,
  useLocalStorage,
  useSubscription,
} from "@dmca/frontend/hooks";
import { SmartCellHomePageWidget } from "@dmca/frontend/widgets/smartcell";
import {
  CellFeaturesEnum,
  type ICellHelpStatus,
  type ICellLocation,
} from "@dmca/models/interface/cell-location";
import {
  Box,
  Badge,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Slider,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { object, optional, string } from "valibot";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const PAN_STEP = 40;
const DRAG_THRESHOLD = 4;
const MAP_CANVAS_LONGEST_SIDE = 1600;

type CellMapEntry = {
  cell: ICellLocation;
  help?: ICellHelpStatus | undefined;
  deviceStatus?: "online" | "offline" | "maintenance";
  noWorkOrders?: boolean;
};

function cellBackground(
  theme: import("@mui/material").Theme,
  status?: "online" | "offline" | "maintenance"
) {
  const color =
    status === "offline"
      ? "error"
      : status === "maintenance"
      ? "warning"
      : "success";
  return status
    ? alpha(
        theme.palette[color].main,
        theme.palette.mode === "light" ? 0.28 : 0.4
      )
    : theme.palette.background.paper;
}

interface PlantMapProps {
  image: string;
  plantName: string;
  configuredCellScale: number;
  canConfigureSize: boolean;
  onCellScaleChange: (scale: number) => Promise<void>;
  canAssign: boolean;
  editPositions: boolean;
  onEditPositionsChange: (editing: boolean) => void;
  openCell: (cell: ICellLocation) => void;
  showCells: boolean;
  placedCells: CellMapEntry[];
  placingCellId?: string;
  onPlace: (cellId: string, x: number, y: number) => void;
  onMove: (cellId: string, x: number, y: number) => Promise<void>;
  selectMapCell: (cellId: string) => void;
  onCancelPlace: () => void;
  canClearMap: boolean;
  onClearMap: () => void;
}

export function PlantMap({
  image,
  plantName,
  configuredCellScale,
  canConfigureSize,
  onCellScaleChange,
  canAssign,
  editPositions,
  onEditPositionsChange,
  openCell,
  showCells,
  placedCells,
  placingCellId,
  onPlace,
  onMove,
  selectMapCell,
  onCancelPlace,
  canClearMap,
  onClearMap,
}: PlantMapProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const markerDragRef = useRef<
    | {
        id: string;
        pointerId: number;
        startX: number;
        startY: number;
        moved: boolean;
      }
    | undefined
  >(undefined);
  const [dragPosition, setDragPosition] = useState<{
    id: string;
    x: number;
    y: number;
  }>();
  const keyboardPositionRef = useRef<
    { id: string; x: number; y: number } | undefined
  >(undefined);
  const [cellScale, setCellScale] = useState(configuredCellScale);
  useEffect(() => setCellScale(configuredCellScale), [configuredCellScale]);
  const dragRef = useRef<
    | {
        pointerId: number;
        startX: number;
        startY: number;
        originX: number;
        originY: number;
        moved: boolean;
      }
    | undefined
  >(undefined);
  const suppressClickRef = useRef(false);
  const [panMode, setPanMode] = useState(false);
  const touchesRef = useRef(new Map<number, { x: number; y: number }>());
  const touchPanRef = useRef<
    | {
        startX: number;
        startY: number;
        originX: number;
        originY: number;
        x: number;
        y: number;
        moved: boolean;
      }
    | undefined
  >(undefined);
  // Remains set until every finger lifts, so a two-finger pan cannot become an edit.
  const blockTouchEditRef = useRef(false);
  const [imageSize, setImageSize] = useState<{
    width: number;
    height: number;
  }>();
  const [view, setView] = useState({ x: 0, y: 0, scale: 1, fitScale: 1 });

  const pointFromPointer = (clientX: number, clientY: number) => {
    const bounds = canvasRef.current!.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (clientX - bounds.left) / bounds.width)),
      y: Math.max(0, Math.min(1, (clientY - bounds.top) / bounds.height)),
    };
  };

  const resetView = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || !imageSize) return;
    const { width, height } = viewport.getBoundingClientRect();
    const paddingX = Math.min(90 * cellScale, width / 4);
    const paddingY = Math.min(54 * cellScale, height / 4);
    const fitScale = Math.min(
      (width - 2 * paddingX) / imageSize.width,
      (height - 2 * paddingY) / imageSize.height
    );
    setView({
      fitScale,
      scale: fitScale,
      x: (width - imageSize.width * fitScale) / 2,
      y: (height - imageSize.height * fitScale) / 2,
    });
  }, [imageSize, cellScale]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !imageSize) return;
    resetView();
    const observer = new ResizeObserver(resetView);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [imageSize, resetView]);

  const zoom = useCallback(
    (factor: number, clientX?: number, clientY?: number) => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      const bounds = viewport.getBoundingClientRect();
      const anchorX =
        clientX === undefined ? bounds.width / 2 : clientX - bounds.left;
      const anchorY =
        clientY === undefined ? bounds.height / 2 : clientY - bounds.top;
      setView((current) => {
        const scale = Math.min(
          current.fitScale * MAX_ZOOM,
          Math.max(current.fitScale * MIN_ZOOM, current.scale * factor)
        );
        const ratio = scale / current.scale;
        return {
          ...current,
          scale,
          x: anchorX - (anchorX - current.x) * ratio,
          y: anchorY - (anchorY - current.y) * ratio,
        };
      });
    },
    []
  );

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      zoom(Math.exp(-event.deltaY * 0.001), event.clientX, event.clientY);
    };
    viewport.addEventListener("wheel", onWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", onWheel);
  }, [zoom]);

  const rebaseTouchPan = () => {
    const points = [...touchesRef.current.values()];
    const x = points.reduce((sum, point) => sum + point.x, 0) / points.length;
    const y = points.reduce((sum, point) => sum + point.y, 0) / points.length;
    const previous = touchPanRef.current;
    const originX = previous?.x ?? view.x;
    const originY = previous?.y ?? view.y;
    touchPanRef.current = {
      startX: x,
      startY: y,
      originX,
      originY,
      x: originX,
      y: originY,
      moved: previous?.moved ?? false,
    };
  };

  const endTouch = (
    event: React.PointerEvent<HTMLDivElement>,
    cancelled = false
  ) => {
    if (!touchesRef.current.delete(event.pointerId)) return;
    if (touchPanRef.current?.moved || blockTouchEditRef.current) {
      suppressClickRef.current = true;
    }
    if (cancelled && markerDragRef.current?.pointerId === event.pointerId) {
      markerDragRef.current = undefined;
      setDragPosition(undefined);
    }
    if (touchesRef.current.size && touchPanRef.current) rebaseTouchPan();
    else touchPanRef.current = undefined;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const commitKeyboardPosition = (cellId: string) => {
    const position = keyboardPositionRef.current;
    if (!position || position.id !== cellId) return;
    keyboardPositionRef.current = undefined;
    void onMove(cellId, position.x, position.y)
      .finally(() => setDragPosition(undefined))
      .catch(() => {});
  };

  return (
    <Box
      ref={viewportRef}
      data-testid="plant-map-viewport"
      aria-label="Plant map. Drag empty space with one finger, use two fingers over cells, enable Pan map, middle mouse drag, or use direction keys to pan; scroll or use plus and minus to zoom; zero to reset. In edit mode, press Enter to place a selected cell at the view center."
      role="region"
      tabIndex={0}
      onClickCapture={(event) => {
        // Keyboard activation remains available after a cancelled pointer gesture.
        if (event.detail === 0 || !suppressClickRef.current) return;
        suppressClickRef.current = false;
        event.preventDefault();
        event.stopPropagation();
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (editPositions && placingCellId && event.key === "Enter") {
          event.preventDefault();
          const bounds = event.currentTarget.getBoundingClientRect();
          const point = pointFromPointer(
            bounds.left + bounds.width / 2,
            bounds.top + bounds.height / 2
          );
          onPlace(placingCellId, point.x, point.y);
          return;
        }
        if (
          [
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
            "+",
            "=",
            "-",
            "0",
          ].includes(event.key)
        ) {
          event.preventDefault();
        }
        if (event.key === "+" || event.key === "=") zoom(1.25);
        else if (event.key === "-") zoom(0.8);
        else if (event.key === "0") resetView();
        else if (event.key.startsWith("Arrow")) {
          setView((current) => ({
            ...current,
            x:
              current.x +
              (event.key === "ArrowRight"
                ? PAN_STEP
                : event.key === "ArrowLeft"
                ? -PAN_STEP
                : 0),
            y:
              current.y +
              (event.key === "ArrowDown"
                ? PAN_STEP
                : event.key === "ArrowUp"
                ? -PAN_STEP
                : 0),
          }));
        }
      }}
      onPointerDownCapture={(event) => {
        if (event.pointerType !== "touch") {
          if (!touchesRef.current.size) suppressClickRef.current = false;
          return;
        }
        if (!touchesRef.current.size) {
          suppressClickRef.current = false;
          blockTouchEditRef.current = false;
        }
        if ((event.target as HTMLElement).closest("[data-map-controls]"))
          return;
        touchesRef.current.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
        });
        if (touchesRef.current.size > 1 || panMode) {
          blockTouchEditRef.current = true;
          suppressClickRef.current = true;
          markerDragRef.current = undefined;
          setDragPosition(undefined);
          rebaseTouchPan();
          for (const pointerId of touchesRef.current.keys()) {
            event.currentTarget.setPointerCapture(pointerId);
          }
        } else if (
          !(event.target as HTMLElement).closest("[data-cell-marker]")
        ) {
          rebaseTouchPan();
        }
      }}
      onPointerMoveCapture={(event) => {
        if (!touchesRef.current.has(event.pointerId)) return;
        touchesRef.current.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
        });
        const pan = touchPanRef.current;
        if (!pan) return;
        const points = [...touchesRef.current.values()];
        const dx =
          points.reduce((sum, point) => sum + point.x, 0) / points.length -
          pan.startX;
        const dy =
          points.reduce((sum, point) => sum + point.y, 0) / points.length -
          pan.startY;
        pan.moved ||= Math.hypot(dx, dy) >= DRAG_THRESHOLD;
        if (!pan.moved) return;
        event.preventDefault();
        suppressClickRef.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        pan.x = pan.originX + dx;
        pan.y = pan.originY + dy;
        setView((current) => ({ ...current, x: pan.x, y: pan.y }));
      }}
      onPointerUpCapture={(event) => endTouch(event)}
      onPointerCancelCapture={(event) => endTouch(event, true)}
      onLostPointerCaptureCapture={(event) => {
        // Transferring capture from a marker to this viewport is not cancellation.
        if (!event.currentTarget.hasPointerCapture(event.pointerId))
          endTouch(event, true);
      }}
      onPointerDown={(event) => {
        if (
          event.pointerType === "touch" ||
          touchesRef.current.size ||
          (event.button !== 1 && !(panMode && event.button === 0)) ||
          (event.target as HTMLElement).closest("[data-map-controls]")
        )
          return;
        event.preventDefault();
        suppressClickRef.current = false;
        event.currentTarget.focus();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          originX: view.x,
          originY: view.y,
          moved: false,
        };
      }}
      onPointerMove={(event) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        const x = event.clientX - drag.startX;
        const y = event.clientY - drag.startY;
        drag.moved ||= Math.hypot(x, y) >= DRAG_THRESHOLD;
        if (drag.moved) {
          setView((current) => ({
            ...current,
            x: drag.originX + x,
            y: drag.originY + y,
          }));
        }
      }}
      onPointerUp={(event) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        suppressClickRef.current = drag.moved;
        dragRef.current = undefined;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      }}
      onPointerCancel={() => {
        dragRef.current = undefined;
      }}
      onAuxClick={(event) => {
        if (event.button === 1) event.preventDefault();
      }}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        overscrollBehavior: "none",
        touchAction: "none",
        userSelect: "none",
        cursor: "default",
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.default",
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
        },
      }}
    >
      <Box
        ref={canvasRef}
        data-testid="plant-map-canvas"
        onClick={(event) => {
          if (
            panMode ||
            !editPositions ||
            !placingCellId ||
            (event.target as HTMLElement).closest("[data-cell-marker]")
          )
            return;
          const { x, y } = pointFromPointer(event.clientX, event.clientY);
          onPlace(placingCellId, x, y);
        }}
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          width: imageSize?.width ?? 0,
          height: imageSize?.height ?? 0,
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          transformOrigin: "top left",
        }}
      >
        <Box
          component="img"
          alt={`${plantName} map`}
          draggable={false}
          src={image}
          onLoad={(event: React.SyntheticEvent<HTMLImageElement>) => {
            const { naturalWidth, naturalHeight } = event.currentTarget;
            const imageScale =
              MAP_CANVAS_LONGEST_SIDE / Math.max(naturalWidth, naturalHeight);
            setImageSize({
              width: naturalWidth * imageScale,
              height: naturalHeight * imageScale,
            });
          }}
          sx={{ display: "block", width: "100%", height: "100%" }}
        />
        {canAssign && editPositions && (
          <Box
            data-testid="plant-grid"
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              backgroundImage: (theme) => {
                const line = alpha(theme.palette.primary.main, 0.65);
                return `linear-gradient(to right, ${line} 1px, transparent 1px), linear-gradient(to bottom, ${line} 1px, transparent 1px)`;
              },
              backgroundSize: "10% 10%",
            }}
          />
        )}
        {showCells &&
          placedCells.map((data) => {
            const cell = data.cell;
            const warnNoWorkOrders =
              data.noWorkOrders &&
              cell.featureFlag.includes(CellFeaturesEnum.RFID);
            const x =
              dragPosition?.id === cell.cellId ? dragPosition.x : cell.mapX!;
            const y =
              dragPosition?.id === cell.cellId ? dragPosition.y : cell.mapY!;
            return (
              <Box
                key={cell.cellId}
                data-cell-marker={cell.cellId}
                data-testid={`map-cell-${cell.cellId}`}
                title={`${cell.locationName}: ${
                  data.deviceStatus ?? "no device status"
                }${data.noWorkOrders ? ", no active work order" : ""}`}
                aria-label={
                  editPositions
                    ? `Move ${cell.locationName}`
                    : cell.locationName
                }
                role={editPositions ? "button" : "link"}
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  if (editPositions && event.key.startsWith("Arrow")) {
                    event.preventDefault();
                    const currentX =
                      keyboardPositionRef.current?.id === cell.cellId
                        ? keyboardPositionRef.current.x
                        : cell.mapX!;
                    const currentY =
                      keyboardPositionRef.current?.id === cell.cellId
                        ? keyboardPositionRef.current.y
                        : cell.mapY!;
                    const nextX = Math.max(
                      0,
                      Math.min(
                        1,
                        currentX +
                          (event.key === "ArrowRight"
                            ? 0.01
                            : event.key === "ArrowLeft"
                            ? -0.01
                            : 0)
                      )
                    );
                    const nextY = Math.max(
                      0,
                      Math.min(
                        1,
                        currentY +
                          (event.key === "ArrowDown"
                            ? 0.01
                            : event.key === "ArrowUp"
                            ? -0.01
                            : 0)
                      )
                    );
                    keyboardPositionRef.current = {
                      id: cell.cellId,
                      x: nextX,
                      y: nextY,
                    };
                    setDragPosition(keyboardPositionRef.current);
                    return;
                  }
                  if (
                    event.key !== "Enter" &&
                    (!editPositions || event.key !== " ")
                  )
                    return;
                  event.preventDefault();
                  if (editPositions) selectMapCell(cell.cellId);
                  else openCell(cell);
                }}
                onKeyUp={(event) => {
                  if (!editPositions || !event.key.startsWith("Arrow")) return;
                  commitKeyboardPosition(cell.cellId);
                }}
                onBlur={() => commitKeyboardPosition(cell.cellId)}
                onClick={(event) => {
                  event.stopPropagation();
                  if (
                    !panMode &&
                    !editPositions &&
                    !(event.target as HTMLElement).closest("a, button")
                  )
                    openCell(cell);
                }}
                onPointerDown={(event) => {
                  if (
                    panMode ||
                    !editPositions ||
                    event.button !== 0 ||
                    (event.pointerType === "touch" && blockTouchEditRef.current)
                  )
                    return;
                  event.stopPropagation();
                  event.currentTarget.setPointerCapture(event.pointerId);
                  markerDragRef.current = {
                    id: cell.cellId,
                    pointerId: event.pointerId,
                    startX: event.clientX,
                    startY: event.clientY,
                    moved: false,
                  };
                }}
                onPointerMove={(event) => {
                  const drag = markerDragRef.current;
                  if (
                    (event.pointerType === "touch" &&
                      blockTouchEditRef.current) ||
                    !drag ||
                    drag.pointerId !== event.pointerId ||
                    drag.id !== cell.cellId
                  )
                    return;
                  drag.moved ||=
                    Math.hypot(
                      event.clientX - drag.startX,
                      event.clientY - drag.startY
                    ) >= DRAG_THRESHOLD;
                  if (!drag.moved) return;
                  setDragPosition({
                    id: cell.cellId,
                    ...pointFromPointer(event.clientX, event.clientY),
                  });
                }}
                onPointerUp={(event) => {
                  const drag = markerDragRef.current;
                  if (
                    (event.pointerType === "touch" &&
                      blockTouchEditRef.current) ||
                    !drag ||
                    drag.pointerId !== event.pointerId ||
                    drag.id !== cell.cellId
                  )
                    return;
                  event.stopPropagation();
                  markerDragRef.current = undefined;
                  if (drag.moved) {
                    const point = pointFromPointer(
                      event.clientX,
                      event.clientY
                    );
                    void onMove(cell.cellId, point.x, point.y)
                      .finally(() => setDragPosition(undefined))
                      .catch(() => {});
                  } else selectMapCell(cell.cellId);
                }}
                onPointerCancel={() => {
                  markerDragRef.current = undefined;
                  setDragPosition(undefined);
                }}
                sx={{
                  position: "absolute",
                  left: `${x * 100}%`,
                  top: `${y * 100}%`,
                  transform: "translate(-50%, -50%)",
                  width: 180 * cellScale,
                  height: 108 * cellScale,
                  containerType: "size",
                  cursor: editPositions ? "move" : "pointer",
                  touchAction: "none",
                  zIndex: 1,
                  "&:focus-visible": {
                    outline: "3px solid",
                    outlineColor: "primary.main",
                  },
                }}
              >
                <SmartCellHomePageWidget
                  cellData={data}
                  data-testid="homepage-widget"
                  sx={{
                    pointerEvents: editPositions ? "none" : "auto",
                    border: "3px solid",
                    borderColor: warnNoWorkOrders ? "#ff1744" : "divider",
                    boxShadow: warnNoWorkOrders
                      ? "0 0 0 2px #ff1744, 0 0 16px 6px #ff1744, 0 0 36px 12px rgba(255, 23, 68, 0.8)"
                      : 3,
                    backgroundColor: (theme) =>
                      cellBackground(theme, data.deviceStatus),
                    overflow: "hidden",
                    p: "min(0.5rem, 3cqi, 3cqb)",
                    gap: "min(0.5rem, 3cqi, 3cqb)",
                    "& .smart-cell-name": {
                      fontSize: "clamp(0.65rem, 9cqi, 1.1rem)",
                    },
                    "& .smart-cell-help-row": {
                      minHeight: 0,
                      p: "min(0.5rem, 2cqi, 2cqb)",
                    },
                  }}
                />
              </Box>
            );
          })}
      </Box>
      {placingCellId && (
        <Stack
          data-map-controls
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            position: "absolute",
            bottom: showCells ? 64 : 8,
            left: 8,
            maxWidth: "calc(100% - 16px)",
            zIndex: 2,
            p: 1,
            backgroundColor: "background.paper",
            boxShadow: 2,
          }}
        >
          <Typography variant="body2">
            Tap or click the map to place the cell
          </Typography>
          <Button size="small" onClick={onCancelPlace}>
            Cancel
          </Button>
        </Stack>
      )}
      {showCells && (
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            zIndex: 2,
            px: 1,
            py: 0.5,
            backgroundColor: "background.paper",
            boxShadow: 1,
          }}
        >
          Green: online · Amber: maintenance · Red fill: offline · Red border:
          no active WO
        </Typography>
      )}
      <Box
        data-map-controls
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 1,
          display: "flex",
          borderRadius: 1,
          backgroundColor: "background.paper",
          boxShadow: 2,
        }}
      >
        <Tooltip title="Pan map: drag anywhere without moving cells">
          <IconButton
            aria-label="Pan map"
            aria-pressed={panMode}
            color={panMode ? "primary" : "default"}
            size="small"
            onClick={() => setPanMode((current) => !current)}
          >
            <PanToolIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom in">
          <IconButton
            aria-label="Zoom in"
            size="small"
            onClick={() => zoom(1.25)}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom out">
          <IconButton
            aria-label="Zoom out"
            size="small"
            onClick={() => zoom(0.8)}
          >
            <RemoveIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset map view">
          <IconButton
            aria-label="Reset map view"
            size="small"
            onClick={resetView}
          >
            <CenterFocusStrongIcon />
          </IconButton>
        </Tooltip>
        {canAssign && showCells && (
          <Tooltip title={editPositions ? "Hide grid" : "Show grid"}>
            <IconButton
              aria-label={editPositions ? "Hide grid" : "Show grid"}
              aria-pressed={editPositions}
              color={editPositions ? "primary" : "default"}
              size="small"
              onClick={() => onEditPositionsChange(!editPositions)}
            >
              <GridOnIcon />
            </IconButton>
          </Tooltip>
        )}
        {canAssign && (
          <Tooltip title="Clear map">
            <span>
              <IconButton
                aria-label="Clear map"
                size="small"
                disabled={!canClearMap}
                onClick={onClearMap}
              >
                <DeleteSweepIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      {showCells && canConfigureSize && (
        <Stack
          data-map-controls
          sx={{
            position: "absolute",
            top: 52,
            right: 8,
            zIndex: 2,
            width: 176,
            px: 2,
            py: 1,
            borderRadius: 1,
            backgroundColor: "background.paper",
            boxShadow: 2,
          }}
        >
          <Typography variant="caption">Cell size</Typography>
          <Slider
            aria-label="Cell size"
            min={0.5}
            max={2}
            step={0.05}
            value={cellScale}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
            onChange={(_, value) => setCellScale(value as number)}
            onChangeCommitted={(_, value) => {
              void onCellScaleChange(value as number).catch(() =>
                setCellScale(configuredCellScale)
              );
            }}
          />
        </Stack>
      )}
    </Box>
  );
}

export const Route = createFileRoute("/_authed/smartcell/")({
  component: RouteComponent,
  validateSearch: object({ plant: optional(string()) }),
  loader: async ({ context }) => {
    await checkPermissionsInLoader("SmartCell::ViewSmartCellOverview", context);
    const { trpcQcProxy: trpc, qc } = context;
    const [cells, helpStatus, plants] = await Promise.all([
      qc.ensureQueryData(trpc.cell.getAll.queryOptions()),
      qc.ensureQueryData(trpc.cell.getHelpStatus.queryOptions()),
      qc.ensureQueryData(trpc.plant.getAll.queryOptions()),
    ]);

    return {
      cells,
      helpStatus,
      plants,
    };
  },
  head: () => ({
    meta: [
      {
        title: "Smart Cell Overview | DNA Dashboard",
      },
    ],
  }),
});

function RouteComponent() {
  const initial = Route.useLoaderData();
  const { plant: requestedPlant } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const hasPermission = useAppPermissions();
  const { data: cells } = useQuery(
    trpc.cell.getAll.queryOptions(undefined, { initialData: initial.cells })
  );

  const { data: helpStatuses } = useQuery(
    trpc.cell.getHelpStatus.queryOptions(undefined, {
      initialData: initial.helpStatus,
    })
  );
  const { data: plants } = useQuery(
    trpc.plant.getAll.queryOptions(undefined, { initialData: initial.plants })
  );
  const [storedPlant, setStoredPlant] = useLocalStorage(
    "smartcell-overview-plant",
    ""
  );
  const plant =
    plants.find((item) => item.id === requestedPlant) ??
    plants.find((item) => item.id === storedPlant) ??
    plants[0];
  const { data: devices } = useQuery(trpc.iot.getAllWithStatus.queryOptions());
  const { data: jobCells } = useQuery({
    ...trpc.productionJob.getSmartCellJobCellIds.queryOptions({
      plantId: plant?.id ?? "",
    }),
    enabled: !!plant,
  });
  const { data: map, isLoading: mapLoading } = useQuery(
    trpc.plant.getMap.queryOptions(
      { plantId: plant?.id ?? "", mapImageKey: plant?.mapImageKey },
      { enabled: !!plant?.mapImageKey }
    )
  );
  const mapActive = !!plant?.mapImageKey && !!map;

  useEffect(() => {
    if (!mapActive) return;
    const overflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = overflow;
    };
  }, [mapActive]);

  useEffect(() => {
    if (plant && plant.id !== requestedPlant) {
      navigate({ search: { plant: plant.id }, replace: true });
    }
  }, [navigate, plant, requestedPlant]);

  useEffect(() => {
    if (plant && storedPlant !== plant.id) setStoredPlant(plant.id);
  }, [plant, storedPlant, setStoredPlant]);

  useSubscription("onCellHelpStatusUpdate", async () => {
    await qc.cancelQueries(trpc.cell.getHelpStatus.queryFilter());
    qc.invalidateQueries(trpc.cell.getHelpStatus.queryFilter());
  });
  useSubscription("onIOTDeviceStatusUpdate", () => {
    qc.invalidateQueries(trpc.iot.getAllWithStatus.queryFilter());
  });
  useSubscription("onProductionJobUpdate", () => {
    qc.invalidateQueries(
      trpc.productionJob.getSmartCellJobCellIds.queryFilter()
    );
  });
  useSubscription("onTagLocationUpdate", () => {
    qc.invalidateQueries(
      trpc.productionJob.getSmartCellJobCellIds.queryFilter()
    );
  });

  const deviceStatuses = useMemo(() => {
    const result = new Map<string, "online" | "offline" | "maintenance">();
    for (const device of devices ?? []) {
      const id = device.cellLocation.cellId;
      const status = device.inMaintenance ? "maintenance" : device.status;
      const current = result.get(id);
      if (
        status === "offline" ||
        (status === "maintenance" && current !== "offline") ||
        !current
      )
        result.set(id, status);
    }
    return result;
  }, [devices]);
  const cellsWithJobs = useMemo(() => new Set(jobCells), [jobCells]);

  const plantCells = cells.filter((cell) => cell.plantId === plant?.id);
  const cellEntries: CellMapEntry[] = plantCells.map((cell) => ({
    cell,
    help: helpStatuses.find((status) => status.cellId === cell.cellId),
    deviceStatus: deviceStatuses.get(cell.cellId),
    noWorkOrders: jobCells ? !cellsWithJobs.has(cell.cellId) : undefined,
  }));
  const [cellMenuAnchor, setCellMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [selectedMapCellId, setSelectedMapCellId] = useState<string>();
  const [placingCellId, setPlacingCellId] = useState<string>();
  const [searchValue, setSearchValue] = useState("");
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [bayFilter, setBayFilter] = useState("");
  const [columnFilter, setColumnFilter] = useState("");
  const [hasWoFilter, setHasWoFilter] = useState(false);
  const [noWoFilter, setNoWoFilter] = useState(false);
  const [deviceFilters, setDeviceFilters] = useState<
    ("online" | "offline" | "maintenance")[]
  >([]);
  const [showCells, setShowCells] = useState(true);
  const [editPositions, setEditPositions] = useState(false);
  const [destinationPlant, setDestinationPlant] = useState("");
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const bulkClearing = useRef(false);
  const search = searchValue.trim().toLowerCase();
  const filtersActive =
    !!bayFilter ||
    !!columnFilter ||
    hasWoFilter ||
    noWoFilter ||
    deviceFilters.length > 0;
  const matchingCells = cellEntries.filter(
    ({ cell, noWorkOrders, deviceStatus }) =>
      (!search || cell.locationName.toLowerCase().includes(search)) &&
      (!bayFilter || cell.bayNumber === Number(bayFilter)) &&
      (!columnFilter || cell.columnNumber === Number(columnFilter)) &&
      ((!hasWoFilter && !noWoFilter) ||
        (hasWoFilter && noWorkOrders === false) ||
        (noWoFilter && noWorkOrders === true)) &&
      (!deviceFilters.length ||
        (deviceStatus !== undefined && deviceFilters.includes(deviceStatus)))
  );
  const placedCells = matchingCells.filter(
    (data) => data.cell.mapX !== null && data.cell.mapY !== null
  );
  const unassignedCellData = matchingCells.filter(
    (data) => data.cell.mapX === null || data.cell.mapY === null
  );

  useEffect(() => {
    setEditPositions(false);
    setPlacingCellId(undefined);
  }, [plant?.id]);
  const updateCellMutation = useMutation(
    trpc.cell.update.mutationOptions({
      onSuccess: async () => {
        if (bulkClearing.current) return;
        await qc.invalidateQueries(trpc.cell.getAll.queryFilter());
        setSelectedMapCellId(undefined);
        setPlacingCellId(undefined);
        setDestinationPlant("");
        toast.success("Cell map updated.");
      },
      onError: (error) => {
        if (!bulkClearing.current) toast.error(error.message);
      },
    })
  );
  const moveCellMutation = useMutation(
    trpc.cell.update.mutationOptions({
      onSuccess: () => qc.invalidateQueries(trpc.cell.getAll.queryFilter()),
      onError: (error) => toast.error(error.message),
    })
  );
  const updatePlantMutation = useMutation(
    trpc.plant.update.mutationOptions({
      onSuccess: (updated) => {
        qc.setQueryData(trpc.plant.getAll.queryKey(), (current) =>
          current?.map((item) => (item.id === updated.id ? updated : item))
        );
      },
      onError: (error) => toast.error(error.message),
    })
  );

  if (!plant) {
    return (
      <>
        <PageHeader>Smart Cell Overview</PageHeader>
        <Typography>No plants configured.</Typography>
      </>
    );
  }

  const openCell = (cell: ICellLocation) =>
    navigate({ to: "/smartcell/$id", params: { id: cell.cellId } });
  const canAssign = hasPermission("Admin::UpdateCell");
  const closeCell = () => {
    setSelectedMapCellId(undefined);
    setDestinationPlant("");
  };
  const selectedCell = selectedMapCellId
    ? plantCells.find((cell) => cell.cellId === selectedMapCellId)
    : undefined;
  const clearMap = async () => {
    const mapped = plantCells.filter((cell) => cell.mapX !== null);
    if (!mapped.length) return;
    bulkClearing.current = true;
    const results = await Promise.allSettled(
      mapped.map((cell) =>
        updateCellMutation.mutateAsync({
          id: cell.cellId,
          mapX: null,
          mapY: null,
        })
      )
    );
    const failed = results.filter(
      (result) => result.status === "rejected"
    ).length;
    bulkClearing.current = false;
    await qc.invalidateQueries(trpc.cell.getAll.queryFilter());
    if (failed)
      toast.error(`${failed} cells could not be removed from the map.`);
    else toast.success("Map cleared.");
  };
  return (
    <>
      <PageHeader>Smart Cell Overview</PageHeader>
      <GlobalNavbarCenter>
        <SearchBar
          placeholder="Search cells"
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          sx={{
            width: { xs: 160, sm: 240, md: 160, lg: 240, xl: 320 },
            maxWidth: "100%",
          }}
        />
        <Tooltip title="Filter cells">
          <IconButton
            aria-label="Filter cells"
            onClick={(event) => setFilterAnchor(event.currentTarget)}
          >
            <Badge color="primary" variant={filtersActive ? "dot" : undefined}>
              <FilterListIcon />
            </Badge>
          </IconButton>
        </Tooltip>
        <Popover
          open={!!filterAnchor}
          anchorEl={filterAnchor}
          onClose={() => setFilterAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Stack spacing={1} sx={{ p: 2, width: 240 }}>
            <Input
              label="Bay #"
              type="number"
              value={bayFilter}
              slotProps={{ htmlInput: { min: 1 } }}
              onChange={(event) => setBayFilter(event.target.value)}
            />
            <Input
              label="Column #"
              type="number"
              value={columnFilter}
              slotProps={{ htmlInput: { min: 1 } }}
              onChange={(event) => setColumnFilter(event.target.value)}
            />
            <Typography variant="subtitle2">Work orders</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasWoFilter}
                  onChange={(event) => setHasWoFilter(event.target.checked)}
                />
              }
              label="Has WO"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={noWoFilter}
                  onChange={(event) => setNoWoFilter(event.target.checked)}
                />
              }
              label="No WO"
            />
            <Typography variant="subtitle2">Device status</Typography>
            {(
              [
                ["online", "Online"],
                ["offline", "Offline"],
                ["maintenance", "In maintenance"],
              ] as const
            ).map(([status, label]) => (
              <FormControlLabel
                key={status}
                control={
                  <Checkbox
                    checked={deviceFilters.includes(status)}
                    onChange={(event) =>
                      setDeviceFilters((current) =>
                        event.target.checked
                          ? [...current, status]
                          : current.filter((item) => item !== status)
                      )
                    }
                  />
                }
                label={label}
              />
            ))}
            <Button
              variant="text"
              disabled={!filtersActive}
              onClick={() => {
                setBayFilter("");
                setColumnFilter("");
                setHasWoFilter(false);
                setNoWoFilter(false);
                setDeviceFilters([]);
              }}
            >
              Clear filters
            </Button>
          </Stack>
        </Popover>
      </GlobalNavbarCenter>
      <GlobalNavbarRight>
        <Select
          aria-label="Plant"
          value={plant.id}
          allowNull={false}
          options={plants.map((item) => ({
            name: item.name,
            value: item.id,
          }))}
          onChange={(event) =>
            navigate({ search: { plant: String(event.target.value) } })
          }
          sx={{ minWidth: 100 }}
        />
        <Tooltip title={showCells ? "Hide cells" : "Show cells"}>
          <IconButton
            aria-label={showCells ? "Hide cells" : "Show cells"}
            aria-pressed={!showCells}
            onClick={() => {
              setShowCells((value) => !value);
              setEditPositions(false);
              setPlacingCellId(undefined);
            }}
          >
            {showCells ? <VisibilityIcon /> : <VisibilityOffIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Navigate to Cell">
          <IconButton
            aria-label="Navigate to Cell"
            onClick={(event) => setCellMenuAnchor(event.currentTarget)}
          >
            <SpaceDashboardIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={cellMenuAnchor}
          open={!!cellMenuAnchor}
          onClose={() => setCellMenuAnchor(null)}
          slotProps={{
            paper: {
              style: {
                maxHeight: "50vh",
              },
            },
          }}
        >
          <Typography
            sx={{
              color: "text.disabled",
              fontSize: "0.875rem",
              pl: 1,
            }}
          >
            Navigate to Cell
          </Typography>
          {cells.map((cell) => (
            <MenuItem key={cell.cellId}>
              <Link
                to="/smartcell/$id"
                params={{ id: cell.cellId }}
                underline="none"
                color="inherit"
              >
                {cell.locationName}
              </Link>
            </MenuItem>
          ))}
        </Menu>
      </GlobalNavbarRight>
      <Box
        sx={{
          height: { xs: "calc(100dvh - 64px)", md: "calc(100dvh - 88px)" },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {mapActive && showCells && !!unassignedCellData.length && (
          <Box
            data-testid="unassigned-cells"
            sx={{ flex: "0 0 20%", minHeight: 0, px: 1, overflow: "auto" }}
          >
            <Typography variant="subtitle2">Unassigned cells</Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ height: mapActive ? 124 : 96 }}
            >
              {unassignedCellData.map((data) => (
                <Stack
                  key={data.cell.cellId}
                  sx={{ minWidth: 200, maxWidth: 240 }}
                >
                  <SmartCellHomePageWidget
                    cellData={data}
                    data-testid="homepage-widget"
                    sx={{ minWidth: 200, maxWidth: 240, height: 96 }}
                  />
                  {canAssign && editPositions && (
                    <Stack direction="row">
                      <Button
                        size="small"
                        onClick={() => setPlacingCellId(data.cell.cellId)}
                      >
                        Place on map
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setSelectedMapCellId(data.cell.cellId)}
                      >
                        Manage
                      </Button>
                    </Stack>
                  )}
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          {plant.mapImageKey && mapLoading ? (
            <Box
              data-testid="plant-map-loading"
              sx={{ height: "100%", display: "grid", placeItems: "center" }}
            >
              <CircularProgress aria-label="Loading map" />
            </Box>
          ) : mapActive ? (
            <PlantMap
              key={plant.id}
              image={map.url}
              plantName={plant.name}
              configuredCellScale={plant.mapCellScale}
              canConfigureSize={hasPermission("Admin::UpdatePlant")}
              onCellScaleChange={(scale) =>
                updatePlantMutation
                  .mutateAsync({
                    id: plant.id,
                    name: plant.name,
                    mapCellScale: scale,
                  })
                  .then(() => {})
              }
              canAssign={canAssign}
              editPositions={editPositions}
              onEditPositionsChange={(editing) => {
                setEditPositions(editing);
                if (!editing) setPlacingCellId(undefined);
              }}
              openCell={openCell}
              showCells={showCells}
              placedCells={placedCells}
              placingCellId={placingCellId}
              onPlace={(cellId, x, y) =>
                updateCellMutation.mutate({
                  id: cellId,
                  mapX: x,
                  mapY: y,
                })
              }
              onMove={(cellId, x, y) =>
                moveCellMutation
                  .mutateAsync({
                    id: cellId,
                    mapX: x,
                    mapY: y,
                  })
                  .then(() => {})
              }
              selectMapCell={setSelectedMapCellId}
              onCancelPlace={() => setPlacingCellId(undefined)}
              canClearMap={
                plantCells.some((cell) => cell.mapX !== null) &&
                !updateCellMutation.isPending
              }
              onClearMap={() => setShowClearConfirmation(true)}
            />
          ) : (
            <Box sx={{ height: "100%", overflow: "auto", p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a plant map to position cells.
              </Typography>
              {showCells && (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={2}
                  data-testid="plant-cells"
                >
                  {matchingCells.map((data) => (
                    <Stack key={data.cell.cellId}>
                      <SmartCellHomePageWidget
                        cellData={data}
                        data-testid="homepage-widget"
                        sx={{
                          width: 220,
                          height: 108,
                          border: "3px solid",
                          borderColor:
                            data.noWorkOrders &&
                            data.cell.featureFlag.includes(
                              CellFeaturesEnum.RFID
                            )
                              ? "error.main"
                              : "divider",
                          backgroundColor: (theme) =>
                            cellBackground(theme, data.deviceStatus),
                        }}
                      />
                      {canAssign && (
                        <Button
                          size="small"
                          onClick={() => setSelectedMapCellId(data.cell.cellId)}
                        >
                          Manage
                        </Button>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Dialog
        open={!!selectedMapCellId}
        onClose={closeCell}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Manage {selectedCell?.locationName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography>
              {selectedCell && selectedCell.mapX !== null
                ? "This cell is on the map."
                : "This cell is unassigned."}
            </Typography>
            <Select
              aria-label="Send to plant"
              value={destinationPlant}
              allowNull={false}
              options={[
                { name: "Send to plant…", value: "" },
                ...plants
                  .filter((item) => item.id !== plant.id)
                  .map((item) => ({ name: item.name, value: item.id })),
              ]}
              onChange={(event) =>
                setDestinationPlant(String(event.target.value))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={
              updateCellMutation.isPending ||
              !selectedCell ||
              selectedCell.mapX === null
            }
            onClick={() =>
              selectedCell &&
              updateCellMutation.mutate({
                id: selectedCell.cellId,
                mapX: null,
                mapY: null,
              })
            }
          >
            Remove from map
          </Button>
          <Button
            disabled={!destinationPlant || updateCellMutation.isPending}
            onClick={() =>
              selectedCell &&
              updateCellMutation.mutate({
                id: selectedCell.cellId,
                plantId: destinationPlant,
                mapX: null,
                mapY: null,
              })
            }
          >
            Send to plant
          </Button>
          <Button
            onClick={() =>
              selectedCell &&
              navigate({
                to: "/admin/cell/$id",
                params: { id: selectedCell.cellId },
              })
            }
          >
            Edit Cell
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationModal
        open={showClearConfirmation}
        onClose={() => setShowClearConfirmation(false)}
        header="Clear map"
        message={`Remove all ${
          plantCells.filter((cell) => cell.mapX !== null).length
        } cells from ${plant.name}'s map?`}
        onConfirm={clearMap}
      />
    </>
  );
}
